import { useEffect } from 'react'
import { useToastStore, type Toast, type ToastTone } from '@/stores/toastStore'
import { cn } from '@/lib/cn'
import { CloseButton } from './CloseCross'

const TONE: Record<ToastTone, string> = {
  default: 'border-blood-900/70',
  success: 'border-eerie/60 shadow-glow-gold',
  danger: 'border-blood-400/70 shadow-glow-blood',
  arcane: 'border-gold/60 shadow-glow-gold',
}

function ToastCard({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismiss)
  useEffect(() => {
    const t = setTimeout(() => dismiss(toast.id), toast.ttl)
    return () => clearTimeout(t)
  }, [toast.id, toast.ttl, dismiss])

  return (
    <div
      role="status"
      className={cn('surface-parchment pointer-events-auto flex w-80 max-w-[90vw] items-start gap-3 border p-3', TONE[toast.tone])}
    >
      <div className="min-w-0 flex-1">
        {toast.title && <p className="title-gothic text-sm text-gold">{toast.title}</p>}
        {toast.html ? (
          <div className="entity-html mt-0.5 font-body text-sm text-bone" dangerouslySetInnerHTML={{ __html: toast.message }} />
        ) : (
          <p className="mt-0.5 font-body text-sm text-bone">{toast.message}</p>
        )}
      </div>
      <CloseButton onClick={() => dismiss(toast.id)} label="Dismiss" glyph={14} box={22} className="-m-2 text-bone-dim hover:text-bone" />
    </div>
  )
}

/** Toast stack (bottom-right). Mounted once in the app shell. */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts)
  if (toasts.length === 0) return null
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[70] flex flex-col gap-2" aria-live="polite">
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  )
}
